import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GenericEventFeed } from './GenericEventFeed';
import {
    useAddEventToReportMenuItem,
    useOpenInNewWindow,
    useWebSocketStatus,
} from '@/hooks';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getDetectionLabel } from '@/utils/detection-classes';
import type { Occurrence } from '@/types/occurrence';
import type { FeedContext } from '@/stores/event-feed.store';
import type { FeedEmptyStateProps } from './components';

interface EntityOccurrenceFeedProps {
    occurrences: Occurrence[];
    isLoading: boolean;
    containerTitle: string;
    navigationFrom: string;
    feedContext: FeedContext;
    onViewAll: () => void;
    titleContent?: ReactNode;
    emptyState?: FeedEmptyStateProps;
    pagination?: {
        hasMore: boolean;
        isLoadingMore: boolean;
        onLoadMore: () => void;
    };
}

export function DirectoryOccurrenceFeed({
    occurrences,
    isLoading,
    containerTitle,
    navigationFrom,
    feedContext,
    onViewAll,
    titleContent,
    emptyState,
    pagination,
}: EntityOccurrenceFeedProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const location = useLocation();
    const isConnected = useWebSocketStatus();
    const { openEvent } = useOpenInNewWindow();
    const addToReportItem = useAddEventToReportMenuItem();

    const navigationState = useMemo(
        () => ({
            from: navigationFrom,
            returnUrl: location.pathname + location.search,
            feedContext,
        }),
        [location.pathname, location.search, navigationFrom, feedContext],
    );

    return (
        <GenericEventFeed
            containerTitle={containerTitle}
            titleContent={titleContent}
            isLive={false}
            isConnected={isConnected}
            onViewAll={onViewAll}
            items={occurrences}
            isLoading={isLoading}
            emptyState={emptyState}
            itemKey={(occurrence) => occurrence.event.id}
            itemEvent={(occurrence) => occurrence.event}
            cardTitle={t('page.event')}
            itemDescription={(occurrence) =>
                getDetectionLabel(t, occurrence.event.classId)
            }
            itemConfidence={(occurrence) =>
                occurrence.confidence != null
                    ? occurrence.confidence * 100
                    : undefined
            }
            itemActionMenuItems={(occurrence, event) =>
                addToReportItem(event, {
                    confidence:
                        occurrence.confidence != null
                            ? occurrence.confidence * 100
                            : undefined,
                })
            }
            onNavigate={(occurrence, clickEvent) =>
                openEvent(occurrence.event.id, clickEvent, {
                    state: navigationState,
                })
            }
            pagination={pagination}
        />
    );
}
